import { useState, useEffect, useCallback, useMemo } from 'react'
import type { ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { useAuthContext } from '../auth'
import { IntegrationService } from '../../services/integrations/integrationService'
import { getIntegrationDisplayName } from '../../utils/integrationDisplay'
import type {
  IntegrationRecord,
  IntegrationPlatform,
  TrelloConfig,
  JiraConfig,
  AzureDevOpsConfig,
} from '@bandos/shared'
import { IntegrationContext, type IntegrationContextType } from './IntegrationContextDef'

/**
 * Note: Integration "ready" state is now determined at the project/mindmap level,
 * not at the user level. Each mindmap stores its own board/list configuration.
 * This context only manages user-level OAuth connections.
 */
export function IntegrationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext()
  const [integrations, setIntegrations] = useState<IntegrationRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedIntegration, setSelectedIntegrationState] = useState<IntegrationPlatform | null>(
    null
  )

  // Helper to get current pathname (works outside Router context)
  const getCurrentPathname = () => {
    if (typeof window !== 'undefined') {
      return window.location.pathname
    }
    return ''
  }

  // Load integrations from API
  const loadIntegrations = useCallback(async () => {
    try {
      setLoading(true)
      const { data, error: serviceError } = await IntegrationService.list()

      if (serviceError) {
        // Silently fail - don't log errors on invite-required page (expected behavior)
        setIntegrations([])
        return
      }

      setIntegrations(data ?? [])
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'integration', action: 'load_integrations' },
      })
      // Silently fail - don't log errors on invite-required page (expected behavior)
      setIntegrations([])
    } finally {
      setLoading(false)
    }
  }, [])

  // Load integrations when user is available, but skip if on invite-required page.
  // Depend on user.id (primitive) rather than the user object to avoid re-fetching
  // when Supabase refreshes the token without changing the actual user.
  const userId = user?.id ?? null

  useEffect(() => {
    const currentPath = getCurrentPathname()
    if (userId && currentPath !== '/invite-required') {
      void loadIntegrations()
    } else if (!userId || currentPath === '/invite-required') {
      // Reset integrations when user logs out or on invite page
      setIntegrations([])
      setLoading(false)
    }
  }, [userId, loadIntegrations])

  // Auto-select single integration when available
  useEffect(() => {
    if (integrations.length === 1 && !selectedIntegration) {
      setSelectedIntegrationState(integrations[0].platform)
    }
  }, [integrations, selectedIntegration])

  // Computed: Connected integrations (all records have platform by definition)
  const connectedIntegrations = useMemo(() => {
    return integrations
  }, [integrations])

  // Computed: Selected integration record
  const selectedIntegrationRecord = useMemo(() => {
    if (!selectedIntegration) return null
    return integrations.find((integration) => integration.platform === selectedIntegration) ?? null
  }, [selectedIntegration, integrations])

  // Computed: Selected integration display name
  const selectedIntegrationDisplayName = useMemo(() => {
    if (!selectedIntegration) return null
    return getIntegrationDisplayName(selectedIntegration)
  }, [selectedIntegration])

  // Action: Set selected integration
  const setSelectedIntegration = useCallback((platform: IntegrationPlatform | null) => {
    setSelectedIntegrationState(platform)
  }, [])

  // Action: Reload integrations
  const reloadIntegrations = useCallback(async () => {
    await loadIntegrations()
  }, [loadIntegrations])

  // Action: Connect/create an integration
  const connectIntegration = useCallback(
    async (
      platform: IntegrationPlatform,
      config: TrelloConfig | JiraConfig | AzureDevOpsConfig | Record<string, unknown>
    ) => {
      try {
        setLoading(true)
        const result = await IntegrationService.connect(platform, config)

        if (result.error) {
          return {
            data: null,
            error: {
              message: result.error.message || 'Failed to connect integration',
              code: result.error.code ?? 'CONNECTION_ERROR',
            },
          }
        }

        // Refresh integrations list after connecting
        if (result.data) {
          await loadIntegrations()
        }

        return {
          data: result.data,
          error: null,
        }
      } catch (error) {
        return {
          data: null,
          error: {
            message: error instanceof Error ? error.message : 'Failed to connect integration',
            code: 'CONNECTION_ERROR',
          },
        }
      } finally {
        setLoading(false)
      }
    },
    [loadIntegrations]
  )

  // Action: Disconnect/delete an integration
  const disconnectIntegration = useCallback(
    async (platform: IntegrationPlatform) => {
      try {
        setLoading(true)
        const result = await IntegrationService.disconnect(platform)

        if (result.error) {
          return {
            data: null,
            error: {
              message: result.error.message || 'Failed to disconnect integration',
              code: result.error.code ?? 'DISCONNECTION_ERROR',
            },
          }
        }

        // Refresh integrations list after disconnecting
        if (result.data) {
          await loadIntegrations()
          // Clear selection if it was the disconnected integration
          if (selectedIntegration === platform) {
            setSelectedIntegrationState(null)
          }
        }

        return {
          data: result.data,
          error: null,
        }
      } catch (error) {
        return {
          data: null,
          error: {
            message: error instanceof Error ? error.message : 'Failed to disconnect integration',
            code: 'DISCONNECTION_ERROR',
          },
        }
      } finally {
        setLoading(false)
      }
    },
    [loadIntegrations, selectedIntegration]
  )

  // Action: Send a requirement node to an integration
  const sendToIntegration = useCallback(
    async (platform: IntegrationPlatform, nodeId: string, mindmapId: string) => {
      try {
        const result = await IntegrationService.sendRequirement(platform, nodeId, mindmapId)

        if (result.error) {
          return {
            data: null,
            error: {
              message: result.error.message || 'Failed to send requirement to integration',
              code: result.error.code ?? 'SEND_ERROR',
            },
          }
        }

        // Refresh integrations to update last_used_at
        if (result.data?.success) {
          await loadIntegrations()
        }

        return {
          data: result.data,
          error: null,
        }
      } catch (error) {
        return {
          data: null,
          error: {
            message:
              error instanceof Error ? error.message : 'Failed to send requirement to integration',
            code: 'SEND_ERROR',
          },
        }
      }
    },
    [loadIntegrations]
  )

  // Helper: Get a specific integration by platform
  const getIntegration = useCallback(
    (platform: IntegrationPlatform): IntegrationRecord | null => {
      return integrations.find((integration) => integration.platform === platform) ?? null
    },
    [integrations]
  )

  // Helper: Check if a platform is connected
  const isConnected = useCallback(
    (platform: IntegrationPlatform): boolean => {
      return integrations.some((integration) => integration.platform === platform)
    },
    [integrations]
  )

  const value = useMemo<IntegrationContextType>(
    () => ({
      // State
      integrations,
      loading,
      selectedIntegration,

      // Computed values
      connectedIntegrations,
      selectedIntegrationRecord,
      selectedIntegrationDisplayName,

      // Actions
      setSelectedIntegration,
      reloadIntegrations,
      connectIntegration,
      disconnectIntegration,
      sendToIntegration,
      getIntegration,
      isConnected,
    }),
    [
      integrations,
      loading,
      selectedIntegration,
      connectedIntegrations,
      selectedIntegrationRecord,
      selectedIntegrationDisplayName,
      setSelectedIntegration,
      reloadIntegrations,
      connectIntegration,
      disconnectIntegration,
      sendToIntegration,
      getIntegration,
      isConnected,
    ]
  )

  return <IntegrationContext.Provider value={value}>{children}</IntegrationContext.Provider>
}
